import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import DataTable from "../../components/DataTable";
import TableTitle from "../../components/TableTitle";
import { CheckIcon, CloseIcon, Icons } from "../Requests/style";
import Avatar from "../../components/Avatar";
import Spinner from "../../components/Spinner";
import ImagePopup from "../../components/ImagePopup";

import {
  bagManagersRequests,
  bagManagers,
  updateBgManagersRequest,
} from "../../services/bagManager";

const requestColumns = [
  "ID",
  "الاسم",
  "الصورة",
  "الايميل",
  "رقم الهاتف",
  "الإجراءات",
];

const managerColumns = [
  "ID",
  "الاسم",
  "الصورة",
  "الايميل",
  "رقم الهاتف",
];

const BagManagerRequests = () => {
  const [imagePopup, setImagePopup] = useState({
    isOpen: false,
    imageUrl: "",
    title: "",
  });

  const queryClient = useQueryClient();

  const { data: waitingData, isLoading: waitingLoading } = useQuery({
    queryKey: ["bagManagerRequests"],
    queryFn: bagManagersRequests,
  });

  const { data: managersData, isLoading: managersLoading } = useQuery({
    queryKey: ["bagManagers"],
    queryFn: bagManagers,
  });

  const waiting = useMemo(
    () => waitingData?.data ?? [],
    [waitingData]
  );

  const managers = useMemo(
    () => managersData?.data ?? [],
    [managersData]
  );

  const acceptMut = useMutation({
    mutationFn: (id) =>
      updateBgManagersRequest(id, {
        accepted: true,
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bagManagerRequests"],
      });

      queryClient.invalidateQueries({
        queryKey: ["bagManagers"],
      });

      toast.success("✅ تم قبول أمين الحقائب");
    },

    onError: (err) => {
      toast.error(err.response?.data?.message || "❌ فشل قبول الطلب");
    },
  });

  const rejectMut = useMutation({
    mutationFn: (id) =>
      updateBgManagersRequest(id, {
        accepted: false,
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bagManagerRequests"],
      });

      queryClient.invalidateQueries({
        queryKey: ["bagManagers"],
      });

      toast.success("❌ تم رفض الطلب");
    },

    onError: (err) => {
      toast.error(err.response?.data?.message || "حدث خطأ أثناء رفض الطلب");
    },
  });

  const waitingRows = waiting.map((u, index) => [
    index + 1,

    u.name ?? "—",

    <Avatar
      key={`waiting-${u._id}`}
      img={u.profile_img}
      name={u.name}
    />,

    u.email ?? "—",

    u.phone_number ?? "—",

    <Icons
      key={`actions-${u._id}`}
      style={{
        display: "flex",
        justifyContent: "center",
        gap: "10px",
      }}
    >
      <CheckIcon
        title="قبول"
        onClick={(e) => {
          e.stopPropagation();
          acceptMut.mutate(u._id);
        }}
        style={{
          cursor: "pointer",
          color: "#2e7d32",
          background: "#e8f5e9",
          padding: "4px",
          borderRadius: "50%",
        }}
      />

      <CloseIcon
        title="رفض"
        onClick={(e) => {
          e.stopPropagation();
          rejectMut.mutate(u._id);
        }}
        style={{
          cursor: "pointer",
          color: "#d32f2f",
          background: "#ffebee",
          padding: "4px",
          borderRadius: "50%",
        }}
      />
    </Icons>,

    `/user-profile/${u._id}`,
  ]);

  const managerRows = managers.map((u, index) => [
    index + 1,

    u.name ?? "—",

    <Avatar
      key={`manager-${u._id}`}
      img={u.profile_img}
      name={u.name}
    />,

    u.email ?? "—",

    u.phone_number ?? "—",

    `/user-profile/${u._id}`,
  ]);

  if (waitingLoading || managersLoading) {
    return <Spinner size="60px" />;
  }

  return (
    <>
      <ImagePopup
        isOpen={imagePopup.isOpen}
        imageUrl={imagePopup.imageUrl}
        title={imagePopup.title}
        onClose={() =>
          setImagePopup({
            isOpen: false,
            imageUrl: "",
            title: "",
          })
        }
      />

      <TableTitle title="طلبات أمين الحقائب" />

      {waiting.length > 0 ? (
        <DataTable columns={requestColumns} data={waitingRows} />
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "30px",
          }}
        >
          🧳 لا يوجد طلبات أمين حقائب
        </div>
      )}

      <div style={{ marginTop: "40px" }} />

      <TableTitle title="أمناء الحقائب المقبولين" />

      {managers.length > 0 ? (
        <DataTable columns={managerColumns} data={managerRows} />
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "30px",
          }}
        >
          🧳 لا يوجد أمناء حقائب
        </div>
      )}
    </>
  );
};

export default BagManagerRequests;